import React, { useMemo } from 'react';
import { Text } from '../../../../atoms/Text/Text';
import { ProgressBar } from '../../../../molecules/ProgressBar/ProgressBar';
import { Table, TableSize } from '../../../../molecules/Table/Table';
import { InlineText } from '../../../../molecules/inputs/inline/InlineText/InlineText';
import { InlineSelect } from '../../../../molecules/inputs/inline/InlineSelect/InlineSelect';
import { InlineVisibilitySelect } from '../../../../molecules/inputs/inline/InlineVisibilitySelect/InlineVisibilitySelect';
import { isEditable, isRetryable } from '../../utils';
/** Resolves the Name cell's secondary meta line -- see `types.ts`'s label doc comments for the
 *  five possible strings this can render. */
function metaFor(item, isUploading, labels) {
    if (item.status === 'done')
        return { text: labels.metaUploaded(item.sizeLabel), isError: false };
    if (item.status === 'finalizing')
        return { text: labels.metaFinalizing(item.sizeLabel), isError: false };
    if (isRetryable(item.status))
        return { text: labels.metaFailed(item.sizeLabel), isError: true };
    if (item.status === 'pending' && isUploading)
        return { text: labels.metaWaiting(item.sizeLabel), isError: false };
    // 'pending' while the batch isn't running, and 'uploading' itself (the ProgressBar below
    // carries the live percentage) both show the plain size line.
    return { text: labels.metaSize(item.sizeLabel), isError: false };
}
function NameCell({ item, isUploading, labels, onNameChange }) {
    const meta = metaFor(item, isUploading, labels);
    const handleBlur = (event) => {
        var _a;
        const target = event.currentTarget;
        const raw = ((_a = target.textContent) !== null && _a !== void 0 ? _a : '').trim();
        if (!raw) {
            // E2E-12: emptying reverts to the ORIGINAL filename, not the current one -- deliberately
            // different from the main attachments table's Name cell (E2E-27), which reverts to the
            // current name instead.
            target.textContent = item.originalName;
            onNameChange(item.id, item.originalName);
            return;
        }
        onNameChange(item.id, raw);
    };
    return (React.createElement("div", { className: "qmb-ui-add-attachments-brushaway__name-cell" },
        React.createElement("span", { className: "qmb-ui-add-attachments-brushaway__name-row" },
            React.createElement(InlineText, { width: "full", disableEdit: !isEditable(item.status), onBlur: handleBlur }, item.name),
            item.extension && (React.createElement(Text, { variant: "body", as: "span", className: "qmb-ui-add-attachments-brushaway__name-extension" },
                ".",
                item.extension))),
        React.createElement(Text, { variant: "x-small", as: "span", className: `qmb-ui-add-attachments-brushaway__meta${meta.isError ? ' qmb-ui-add-attachments-brushaway__meta--error' : ''}` }, meta.text),
        item.status === 'uploading' && (React.createElement(ProgressBar, { value: item.progress, className: "qmb-ui-add-attachments-brushaway__progress" }))));
}
function CategoryCell({ item, config }) {
    return (React.createElement(InlineSelect, { options: config.options.map((option) => ({ value: option.value, label: option.label })), value: item.categoryValue, onChange: (value) => config.onChange(item.id, value), disabled: !isEditable(item.status), showSearch: true, footerAction: config.addCategoryLabel && config.onRequestAddCategory
            ? {
                label: config.addCategoryLabel,
                onSelect: () => { var _a; return (_a = config.onRequestAddCategory) === null || _a === void 0 ? void 0 : _a.call(config, (createdCategoryId) => config.onChange(item.id, createdCategoryId)); }
            }
            : undefined }));
}
function VisibilityCell({ item, config }) {
    return (React.createElement(InlineVisibilitySelect, { options: config.options, values: item.visibilityValues, summary: config.getSummary(item.visibilityValues), onChange: (values) => config.onChange(item.id, values), disabled: !isEditable(item.status) }));
}
function ActionsCell({ item, labels, onRetryItem, onRemoveItem }) {
    const fileName = item.extension ? `${item.name}.${item.extension}` : item.name;
    return (React.createElement("span", { className: "qmb-ui-add-attachments-brushaway__row-actions" },
        isRetryable(item.status) && (React.createElement("button", { type: "button", className: "qmb-ui-add-attachments-brushaway__row-action", "aria-label": labels.retryActionLabel, onClick: (event) => {
                event.stopPropagation();
                onRetryItem(item.id);
            } },
            React.createElement("i", { className: "fa-light fa-arrow-rotate-right", "aria-hidden": "true" }))),
        item.status !== 'uploading' && (React.createElement("button", { type: "button", className: "qmb-ui-add-attachments-brushaway__row-action", "aria-label": labels.removeActionLabel(fileName), onClick: (event) => {
                event.stopPropagation();
                onRemoveItem(item.id);
            } },
            React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" })))));
}
/**
 * The staged-queue table rendered via `FileHandler`'s `renderList` render-prop. Deliberately NOT
 * `FileHandler`'s own built-in Preview/Name/Type/actions columns -- the design has no Preview
 * column and no Size column (folded into the Name cell's meta line instead), so the hardcoded
 * default table is the wrong shape for this surface (`docs/ai-knowledge/frontend/file-handler.md`).
 */
export function QueueTable({ items, isUploading, columns, labels, onNameChange, onRetryItem, onRemoveItem }) {
    const tableColumns = useMemo(() => [
        {
            dataKey: 'name',
            label: labels.columnName,
            columnType: 'primary',
            renderer: ({ item }) => (React.createElement(NameCell, { item: item, isUploading: isUploading, labels: labels, onNameChange: onNameChange }))
        },
        {
            dataKey: 'categoryValue',
            label: labels.columnCategory,
            width: '180px',
            renderer: ({ item }) => React.createElement(CategoryCell, { item: item, config: columns.category })
        },
        {
            dataKey: 'visibilityValues',
            label: labels.columnVisibleTo,
            width: '180px',
            renderer: ({ item }) => React.createElement(VisibilityCell, { item: item, config: columns.visibility })
        },
        {
            dataKey: 'typeLabel',
            label: labels.columnType,
            width: '72px'
        },
        {
            dataKey: 'actions',
            label: '',
            columnType: 'action',
            renderer: ({ item }) => (React.createElement(ActionsCell, { item: item, labels: labels, onRetryItem: onRetryItem, onRemoveItem: onRemoveItem }))
        }
    ], [labels, isUploading, columns, onNameChange, onRetryItem, onRemoveItem]);
    return (React.createElement(Table, { className: "qmb-ui-add-attachments-brushaway__queue-table", variant: "detail", size: TableSize.Full, selectable: false, columns: tableColumns, data: items, getItemId: (item) => item.id }));
}
